import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import CustomMultiSelect from '../../../../components/shared/CustomMultiSelect/CustomMultiSelect';
import FiltersDrawer from '../../../../components/shared/FiltersDrawer/FiltersDrawer';
import { getDisputeFiltersData } from '../../../../redux/actions/disputeActions';
import { resetDisputeFilters, setDisputeFilters } from '../../../../redux/actions/filtersActions';

import CustomHeader from './CalendarComponents/CustomHeader/CustomHeader';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './DisputeFilters.module.scss';

const arrayToSelectObject = (array) =>
  array?.map((val, idx) => {
    if (Array.isArray(val)) {
      return { id: idx, label: val.join(' '), value: val };
    }
    return { id: idx, label: val, value: val };
  });

const SelectField = ({ heading, subHeading, placeholder, options, value, onChange }) => (
  <div>
    {heading && <p className={styles.heading}>{heading}</p>}
    {subHeading && <p className={styles.subHeading}>{subHeading}</p>}
    <CustomMultiSelect placeholder={placeholder} options={options} value={value} onChange={onChange} />
  </div>
);

const today = new Date();

const DateField = ({ label, selectedDate, setSelectedDate, startDate, endDate, filters_key, ...props }) => {
  const handleDayClassName = useCallback(
    (date) => {
      if (date.toDateString() === startDate.toDateString() || date.toDateString() === endDate.toDateString())
        return styles.selectedDay;
      if (date > startDate && date < endDate) return styles.inRangeDays;
      return styles.days;
    },
    [startDate, endDate]
  );
  return (
    <div className={styles.inputFieldContainer}>
      <p className={styles.inputLabel}>{label}</p>
      <DatePicker
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <CustomHeader
            date={date}
            changeYear={changeYear}
            changeMonth={changeMonth}
            decreaseMonth={decreaseMonth}
            increaseMonth={increaseMonth}
            prevMonthButtonDisabled={prevMonthButtonDisabled}
            nextMonthButtonDisabled={nextMonthButtonDisabled}
          />
        )}
        selected={selectedDate}
        onChange={(date) => setSelectedDate(filters_key, new Date(date.getTime() - date.getTimezoneOffset() * 60000))}
        startDate={startDate}
        endDate={endDate}
        dateFormat="MMMM dd, yyyy"
        className={styles.inputField}
        calendarClassName={styles.calendar}
        showPopperArrow={false}
        dayClassName={handleDayClassName}
        {...props}
      />
    </div>
  );
};

const RewardField = ({ heading, subHeading, label, value, ...props }) => (
  <div className={styles.inputFieldContainer}>
    <p className={styles.inputLabel}>{label}</p>
    <p className={styles.currencyLabel}>{value && '$'}</p>
    <input className={`${styles.inputField} ${styles.amountField}`} type="number" value={value} {...props} />
  </div>
);

const DisputeFilters = ({ toggleFilters, filtersDrawer, getFilteredDisputes }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.filtersReducer.disputeFilters);
  const [filtersData, setFiltersData] = useState(filters);

  const company_names = useSelector((state) => state.disputesReducer.disputeFilters.company_names);
  const openings = useSelector((state) => state.disputesReducer.disputeFilters.openings);
  const experiential_rewards = useSelector((state) => state.disputesReducer.disputeFilters.experiential_rewards);
  const candidate_names = useSelector((state) => state.disputesReducer.disputeFilters.candidate_names);

  const updateFilters = useCallback(() => {
    dispatch(setDisputeFilters(filtersData));
    toggleFilters();
    getFilteredDisputes(filtersData);
  }, [filtersData]);

  const handleFilterChange = useCallback((key, value) => {
    setFiltersData((oldFiltersData) => {
      const updatedFilters = { ...oldFiltersData };
      updatedFilters[key] = value;
      return updatedFilters;
    });
  }, []);

  const handleResetFilters = useCallback(() => {
    dispatch(resetDisputeFilters());
  }, []);

  useEffect(() => {
    setFiltersData(filters);
  }, [filters]);

  useEffect(() => {
    dispatch(getDisputeFiltersData());
  }, []);

  const newFiltersCount = useMemo(() => {
    let count = 0;
    if (filtersData.statuses.length) count += 1;
    if (filtersData.openings.length) count += 1;
    if (filtersData.company_names.length) count += 1;
    if (filtersData.candidate_names.length) count += 1;
    if (filtersData.start_date) count += 1;
    if (filtersData.end_date) count += 1;
    if (filtersData.min_amount) count += 1;
    if (filtersData.max_amount) count += 1;
    if (filtersData.experiential_rewards.length) count += 1;

    return count;
  }, [filtersData]);

  return (
    <FiltersDrawer
      open={filtersDrawer}
      handleToggleDrawer={toggleFilters}
      handleShowResults={updateFilters}
      filtersCount={newFiltersCount}
      handleResetFilters={handleResetFilters}
    >
      <div className={styles.filtersContainer}>
        <SelectField
          heading={t('filterHeadings.opening')}
          placeholder={t('placeholders.opening')}
          options={arrayToSelectObject(openings) || []}
          value={filtersData.openings}
          onChange={(options) => handleFilterChange('openings', options)}
        />
        <SelectField
          heading={t('filterHeadings.companyName')}
          placeholder={t('placeholders.companyName')}
          options={arrayToSelectObject(company_names) || []}
          value={filtersData.company_names}
          onChange={(options) => handleFilterChange('company_names', options)}
        />
        <SelectField
          heading={t('filterHeadings.candidateName')}
          placeholder={t('placeholders.candidateName')}
          options={arrayToSelectObject(candidate_names) || []}
          value={filtersData.candidate_names}
          onChange={(options) => handleFilterChange('candidate_names', options)}
        />
        <div className={styles.inputContainer}>
          <p className={styles.heading}>{t('filterHeadings.dueDate')}</p>
          <DateField
            title={t('formLabels.dueDate')}
            label={t('formLabels.from')}
            selectedDate={filtersData.start_date ?? null}
            setSelectedDate={handleFilterChange}
            selectsStart
            startDate={filtersData.start_date === '' ? today : filtersData.start_date}
            endDate={filtersData.end_date === '' ? today : filtersData.end_date}
            dateFormatCalendar="MMMM"
            filters_key="start_date"
            maxDate={today}
          />
          <DateField
            label={t('formLabels.to')}
            selectedDate={filtersData.end_date ?? null}
            setSelectedDate={handleFilterChange}
            filters_key="end_date"
            showMonthDropdown
            showYearDropdown
            selectsEnd
            startDate={filtersData.start_date === '' ? today : filtersData.start_date}
            endDate={filtersData.end_date === '' ? today : filtersData.end_date}
            dateFormatCalendar="MMMM"
            minDate={filtersData.start_date === '' ? today : filtersData.start_date}
            maxDate={today}
          />
        </div>
        <div className={styles.inputContainer}>
          <div>
            <p className={styles.heading}>{t('filterHeadings.reward')}</p>
            <p className={styles.subHeading}>{t('filterHeadings.monetaryReward')}</p>
          </div>
          <RewardField
            label={t('formLabels.min')}
            value={filtersData.min_amount}
            min="1"
            onChange={(e) => handleFilterChange('min_amount', e.target.value)}
          />
          <RewardField
            label={t('formLabels.max')}
            value={filtersData.max_amount}
            min="1"
            onChange={(e) => handleFilterChange('max_amount', e.target.value)}
          />
        </div>
        <SelectField
          subHeading={t('dispute.experientialReward')}
          placeholder={t('placeholders.experientialReward')}
          options={arrayToSelectObject(experiential_rewards) || []}
          value={filtersData.experiential_rewards}
          onChange={(options) => handleFilterChange('experiential_rewards', options)}
        />
      </div>
    </FiltersDrawer>
  );
};

export default DisputeFilters;
